import { JSX, useMemo } from "react";

import { Theme, useTheme } from "@mui/material";
import { type LatLngBoundsLiteral, type LatLngExpression } from "leaflet";
import { MapContainer } from "react-leaflet";
import ChartSkeleton from "src/components/ChartSkeleton/ChartSkeleton";
import "leaflet/dist/leaflet.css";
import "../../../../lib/leafletSetup.helper";
import FlowMapLegend from "src/components/Map/Controls/FlowMapLegend/FlowMapLegend";
import FileFlowMapLayer from "src/components/Map/MapLayers/FileFlowMapLayer/FileFlowMapLayer";
import {
  logarithmicThicknessFunctionWithZoom,
  defaultColoringFunction,
} from "src/components/Map/MapLayers/FileFlowMapLayer/FileFlowMapLayer.helpers";
import GeoMapLayer from "src/components/Map/MapLayers/GeoMapLayer";
import useLocationDashboardStore from "src/store/locationDashboard.store";
import { type FlowMapWithFrequencyFeatureCollection } from "src/types/FlowMapWithFrequencyFeatureCollection";

import { calculateFlowmapCenterAndBounds } from "./FileFlowMap.helper";

const styles = (theme: Theme) => ({
  mapContainer: {
    height: `calc(100vh - ${theme.spacing(32.5)})`,
    minHeight: theme.spacing(43.75),
    width: "100%",
  },
});

interface BoundingBox {
  center: LatLngExpression;
  bounds: LatLngBoundsLiteral;
  locationId: string | undefined;
}

function FileFlowMap({ fileContent }: { fileContent: FlowMapWithFrequencyFeatureCollection }): JSX.Element | null {
  const theme = useTheme();
  const { currentLocationId } = useLocationDashboardStore();

  const boundingBox: BoundingBox = useMemo(() => {
    const { center: newCenter, bounds: newBounds } = calculateFlowmapCenterAndBounds(fileContent);
    return { center: newCenter, bounds: newBounds, locationId: currentLocationId };
  }, [fileContent, currentLocationId]);

  const isLoading = !boundingBox;

  if (!fileContent) return null;
  if (isLoading) return <ChartSkeleton />;

  return (
    <MapContainer
      style={styles(theme).mapContainer}
      center={boundingBox.center}
      bounds={boundingBox.bounds}
      attributionControl={false}
      preferCanvas
    >
      <GeoMapLayer />
      <FileFlowMapLayer
        flowMapRoutes={fileContent}
        thicknessFunction={logarithmicThicknessFunctionWithZoom}
        coloringFunction={defaultColoringFunction}
      />
      <FlowMapLegend flowMapRoutes={fileContent} />
    </MapContainer>
  );
}

export default FileFlowMap;
